import { P, match } from 'ts-pattern';

import { isSoccerLeague } from '../../services/interfaces/propsService/api/leagues';
import type { PublicGameWithTeams } from '../../services/interfaces/propsService/api';
import type { TPropExpanded, TUserProp } from '../../services/interfaces/propsService/parsed/props';

type MatchupTeam = {
  alias?: string;
  id?: string;
};

export type PlayerTeamFirstMatchup = {
  leftAlias: string;
  rightAlias: string;
  separator: '@' | 'vs.';
  highlightedAlias: string | null;
};

// Soccer competitions render matchups home-first ("home vs. away"), unlike the
// rest of the league set which uses the player-team-first convention. Driven
// off the SOCCER_LEAGUE_ALIASES list so adding a new soccer competition there
// auto-enables home-first ordering everywhere this helper is used.
export function shouldRenderHomeFirst(league: string | undefined): boolean {
  return isSoccerLeague(league);
}

export function teamIdForLeagueImage(prop: TPropExpanded | TUserProp): string | undefined {
  return prop.team_id ?? prop.team?.id ?? prop.entity?.current_team_id;
}

/**
 * Team id for CDN logo on pick rows. Prefer explicit prop fields; then lineup membership;
 * then match `prop.team.alias` to the game when the API omits `team_id` but includes team context.
 */
export function playerTeamIdForPick(
  prop: TPropExpanded | TUserProp,
  game: PublicGameWithTeams
): string {
  const direct = teamIdForLeagueImage(prop);
  if (direct) return direct;

  const entityId = prop.entity?.id ?? prop.entity_id;
  if (entityId) {
    if (game.home.lineup?.some((m) => m.id === entityId)) {
      return game.home.id;
    }
    if (game.away.lineup?.some((m) => m.id === entityId)) {
      return game.away.id;
    }
  }

  const alias = prop.team?.alias;
  if (alias) {
    if (game.home.alias === alias) {
      return game.home.id;
    }
    if (game.away.alias === alias) {
      return game.away.id;
    }
  }

  return prop.team_id ?? '';
}

export function getPlayerTeamFirstMatchup(params: {
  awayTeam?: MatchupTeam;
  homeTeam?: MatchupTeam;
  playerTeamId?: string;
  league?: string;
}): PlayerTeamFirstMatchup | null {
  const awayTeamAlias = params?.awayTeam?.alias;
  const homeTeamAlias = params?.homeTeam?.alias;

  if (!awayTeamAlias || !homeTeamAlias) return null;
  const awayTeamId = params?.awayTeam?.id;
  const homeTeamId = params?.homeTeam?.id;
  const { playerTeamId } = params;

  if (shouldRenderHomeFirst(params.league)) {
    const highlightedAlias = match(playerTeamId)
      .with(
        P.when((id) => !!(id && homeTeamId && id === homeTeamId)),
        () => homeTeamAlias
      )
      .with(
        P.when((id) => !!(id && awayTeamId && id === awayTeamId)),
        () => awayTeamAlias
      )
      .otherwise(() => null);

    return {
      leftAlias: homeTeamAlias,
      rightAlias: awayTeamAlias,
      separator: 'vs.' as const,
      highlightedAlias,
    };
  }

  return match(playerTeamId)
    .with(
      P.when((id) => !!(id && homeTeamId && id === homeTeamId)),
      () => ({
        leftAlias: homeTeamAlias,
        rightAlias: awayTeamAlias,
        separator: 'vs.' as const,
        highlightedAlias: homeTeamAlias,
      })
    )
    .with(
      P.when((id) => !!(id && awayTeamId && id === awayTeamId)),
      () => ({
        leftAlias: awayTeamAlias,
        rightAlias: homeTeamAlias,
        separator: '@' as const,
        highlightedAlias: awayTeamAlias,
      })
    )
    .otherwise(() => ({
      leftAlias: awayTeamAlias,
      rightAlias: homeTeamAlias,
      separator: '@' as const,
      highlightedAlias: null,
    }));
}

export function formatPlayerTeamFirstGameDescription(params: {
  awayTeam?: { alias?: string; id?: string };
  homeTeam?: { alias?: string; id?: string };
  playerTeamId?: string;
  league?: string;
}): string {
  const matchup = getPlayerTeamFirstMatchup(params);
  if (!matchup) return '—';

  return `${matchup.leftAlias} ${matchup.separator} ${matchup.rightAlias}`;
}

export function formatGameDescriptionWithScores(
  gameDescription: string,
  homeAlias: string | undefined,
  awayAlias: string | undefined,
  homePoints: number,
  awayPoints: number,
): string {
  if (!homeAlias || !awayAlias) return gameDescription;

  const isAtSep = gameDescription.includes(' @ ');
  const sepToken = isAtSep ? ' @ ' : ' vs. ';
  const parts = gameDescription.split(sepToken);
  if (parts.length !== 2) return gameDescription;

  const [leftRaw, rightRaw] = parts;
  const left = leftRaw.trim();
  const right = rightRaw.trim();
  if ((left !== homeAlias && left !== awayAlias) || (right !== homeAlias && right !== awayAlias)) {
    return gameDescription;
  }

  const sep = isAtSep ? '@' : 'vs.';
  const leftScore = left === homeAlias ? homePoints : awayPoints;
  const rightScore = right === homeAlias ? homePoints : awayPoints;

  return `${left} ${leftScore} ${sep} ${right} ${rightScore}`;
}
